import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  queryCompanyViewKey,
  queryCompanyViews,
  mutateCompanyViewKey,
  mutateCompanyViews,
  getMutateCompanyViews,
} from './queries';
import { formatDistanceToNow } from 'date-fns';
import { useUserAccess } from '@/hooks/useUserAccess';

export function useCompanyLimits(companyId?: string) {
  const [visited, setVisited] = useState(false);
  const { status, data, refetch } = useQuery(
    queryCompanyViewKey,
    queryCompanyViews,
    {
      keepPreviousData: true,
      staleTime: Infinity,
      enabled: false,
      select: (response) => {
        if (typeof response === 'undefined') {
          return { companies: [], views: 0, limit: Infinity };
        }
        return {
          companies: response.data.map((company) => {
            return {
              id: company.company_id,
              name: company.name,
              expiryTime: formatDistanceToNow(company.expiry_time),
              uniqueSymbol: company.unique_symbol,
              canonicalUrl: company.canonical_url,
            };
          }),
          views: response.meta.total,
          limit: response.meta.limit,
          restricted: response.meta.total === response.meta.limit,
        };
      },
    }
  );

  useEffect(() => {
    if (data) return;
    refetch();
  }, [data]);

  useEffect(() => {
    if (companyId !== undefined) {
      const visited =
        data?.companies.findIndex((company) => company.id === companyId) !== -1;
      setVisited(visited);
    }
  }, [data, companyId]);

  return {
    status,
    companies: data?.companies || [],
    views: data?.views || 0,
    limit: data?.limit || 0,
    restricted: data?.restricted || false,
    visited,
  };
}

export function useUpdateCompanyLimits(companyId?: string) {
  const [restricted, setRestricted] = useState(false);
  const [status, setStatus] = useState<
    'idle' | 'error' | 'loading' | 'success'
  >('idle');
  const uac = useUserAccess();
  const limits = useCompanyLimits();
  const client = useQueryClient();
  const mutateLimits = useQuery(
    [mutateCompanyViewKey, companyId],
    mutateCompanyViews,
    {
      staleTime: Infinity,
      enabled: false,
      onSuccess: (response) => {
        client.setQueryData(queryCompanyViewKey, response);
      },
    }
  );
  useEffect(() => {
    if (
      uac.status === 'success' &&
      limits.status === 'success' &&
      companyId !== undefined
    ) {
      if (typeof uac.data !== 'undefined') {
        const validCompany =
          limits.companies.findIndex((company) => company.id === companyId) !==
          -1;
        const viewCountMaxed =
          limits.companies.length === uac.data.viewCompanyReports;

        if (validCompany) {
          /** current company is in users valid list */
          setRestricted(false);
          setStatus('success');
        } else if (viewCountMaxed) {
          /** maxed out company limit */
          setRestricted(true);
          setStatus('success');
          //Only update company limit if user has a plan (i.e. logged in)
        } else if (mutateLimits.status === 'idle' && uac.data.planType) {
          /**  */
          mutateLimits.refetch();
        }
      }
    }
  }, [
    uac.status,
    uac.data,
    status,
    mutateLimits,
    limits.companies,
    limits.status,
    companyId,
  ]);

  useEffect(() => {
    const needsInvalidation =
      limits.companies.findIndex((company) => company.id === companyId) === -1;
    if (
      mutateLimits.status === 'success' &&
      typeof mutateLimits.data !== 'undefined' &&
      needsInvalidation
    ) {
      setStatus('success');
      setRestricted(mutateLimits.data.meta.plan_restricted);
    }
  }, [mutateLimits, limits, companyId]);

  return {
    status,
    restricted,
  };
}

export function useGetMutateCompanyLimit() {
  const client = useQueryClient();
  const { mutate: mutateCompanyView, status: mutateCompanyViewStatus } =
    useMutation(getMutateCompanyViews, {
      onSuccess(data) {
        client.setQueryData(queryCompanyViewKey, data);
      },
    });

  return { mutateCompanyView, mutateCompanyViewStatus };
}
